/**
 * Net worth — everything the client owns, less everything they owe, as a pure function.
 *
 * The simplest of the assessments, with two things in it worth stating:
 *
 *   - THE HOME COUNTS. Unlike the means tests, where the family home is exempt while someone lives in
 *     it, net worth is what the client is actually worth — so the home is in, at its full value,
 *     always.
 *   - AN ANNUITY IS WORTH ITS RESIDUAL, not its purchase price. A lifetime annuity's `value` is what was
 *     put into it; what the client (or their estate) could still get back is `residualValue`, which
 *     declines to nothing over the guarantee period. Where that field is set it is what counts.
 *
 * Extracted from `calculateFinancials`'s "--- 7. Final Cash Flow & Net Worth ---" block, which still
 * calls it — through the injectable {@link NetWorthAssessor} seam, so the projection engine can hand in
 * the assessor owned by its netWorthPlugin.
 *
 * NOTE a sibling that is deliberately NOT merged into this: the netWorthPlugin has its own
 * `sumAssetValues`, which walks the same assets with the same annuity rule but ALSO skips non-object
 * entries and non-finite values, and emits debug logging. On well-formed data the two agree exactly.
 * They part company on two inputs only, both of which the HTTP boundary's numeric schema should already
 * have excluded:
 *   - an `Infinity` value propagates into the total here, where the plugin skips it;
 *   - a `null` entry in an asset list makes this walk THROW, where the plugin skips it.
 * (A `NaN` value behaves identically in both — `NaN || 0` is 0.) Unifying them would therefore change
 * what `calculateFinancials` returns, or whether it returns at all, so they are left as they are: the
 * projection reports the plugin's figure and this is what a standalone caller gets. See
 * netWorthPlugin.test.ts, which pins the difference.
 */

import { annuityNetWorthValueOf } from '../../../../../shared/src/calculation/annuityStrategies.ts';
import type { FactFind } from '../../../../../shared/src/calculation/factFindTypes.ts';
import type { StrategyOptions } from '../../../../../shared/src/calculation/strategyOptionsTypes.ts';
import { villageInterestHeld, villageExitEntitlement, todayIsoOf } from '../../../../../shared/src/calculation/support/villageExit.ts';
import { DEFAULT_HOME_GROWTH_PCT } from '../../../../../shared/src/calculation/assetRateOverrides.ts';

/** The balance sheet to be totalled. */
export interface NetWorthInput {
    /** Read for `home.value`, `assets`, `liabilities` and the village record. */
    factFind: FactFind;
    /** Read for `villageInterestSold` (a plan that has been paid out). Optional: none means held. */
    strategyOptions?: StrategyOptions;
    /** The day the balance sheet is struck on; today where absent. Values a held village interest to that day. */
    asOfDate?: Date | string;
}

/**
 * A RETIREMENT VILLAGE INTEREST IS WEALTH (owner, 8 Oct 2026). The entry contribution comes back on
 * leaving, less the departure fee, like the refundable deposit in care — so a resident's balance
 * sheet carries it at its exit entitlement on the day it is struck, exactly as a homeowner's carries
 * the home. Nothing for the pension changes (the interest is exempt or counted by the extra
 * allowable amount as before); only what the client is worth. Zero once the plan has paid it out.
 */
export function villageInterestValue(factFind: FactFind, strategyOptions: StrategyOptions = {}, asOfDate?: Date | string): number {
    const today = todayIsoOf(new Date());
    const asAt = asOfDate ? todayIsoOf(new Date(asOfDate)) : today;
    // An OLD unit awaiting its payout after a move to another village or a granny flat (8 Oct 2026):
    // worth what it will pay until the day it does.
    const pending = strategyOptions.villageExit;
    const oldUnitPending = pending && !strategyOptions.villageInterestSold && pending.refundDate && asAt < pending.refundDate ? Math.max(0, Number(pending.entitlement) || 0) : 0;
    // A unit given up with its payout still to come is worth what it will pay — the entitlement fixed
    // on the day it was given up — not a resale that keeps growing (9 Oct 2026).
    if (oldUnitPending > 0) return oldUnitPending;
    // The village MODULE (a move IN) carries its own contract on the run's options and adds the
    // refundable contribution to its day-one figure itself (retirementVillageProjections.ts); its
    // plugin values every later year. Here, for its runs, only the old unit's payout.
    if ((strategyOptions as { retirementVillage?: unknown }).retirementVillage) return oldUnitPending;
    if (!villageInterestHeld(factFind, strategyOptions)) return oldUnitPending;
    const e = villageExitEntitlement(factFind, asAt, { resaleGrowthPct: DEFAULT_HOME_GROWTH_PCT, resaleBaseDate: today });
    return e ? Math.max(0, e.entitlement) : 0;
}

/** What the client is worth. */
export interface NetWorthAssessment {
    /** Assets less liabilities. Negative for a client who owes more than they own. */
    netWorth: number;
    /** Every asset at its value (annuities at their residual), plus the home. */
    totalAssetsValue: number;
    /** Every liability's outstanding balance. Excludes the two strategy loans, which the engine tracks apart. */
    totalLiabilities: number;
}

/**
 * Assesses net worth. Injected into `calculateFinancials` so a caller can own the rules — see
 * {@link calculateNetWorthPosition}, the default, and the projection engine's netWorthPlugin.
 */
export type NetWorthAssessor = (input: NetWorthInput) => NetWorthAssessment;

/** Totals the balance sheet: the home plus every asset, less every liability. */
export const calculateNetWorthPosition: NetWorthAssessor = ({ factFind, strategyOptions = {}, asOfDate }: NetWorthInput): NetWorthAssessment => {
    const { home, assets, liabilities } = factFind;

    let totalAssetsValue = 0;

    if (home && home.value) {
        totalAssetsValue += home.value || 0;
    }
    totalAssetsValue += villageInterestValue(factFind, strategyOptions, asOfDate);

    Object.values(assets || {}).forEach((assetList) => {
        (assetList || []).forEach((asset) => {
            if (asset.type === 'annuities') {
                // A lifetime annuity is worth its residual; a fixed term the capital still to be
                // returned (its reducing `value`) — see annuityNetWorthValueOf.
                totalAssetsValue += annuityNetWorthValueOf(asset);
            } else {
                totalAssetsValue += asset.value || 0;
            }
        });
    });

    let totalLiabilities = 0;
    Object.values(liabilities || {}).forEach((list) => (list || []).forEach((item) => (totalLiabilities += item.balance || 0)));

    return { netWorth: totalAssetsValue - totalLiabilities, totalAssetsValue, totalLiabilities };
};
